import { useCallback, useEffect, useMemo, useRef } from 'react';
import { isEqual } from 'lodash';

import centralIntakeBranch from 'components/dischargeForm/centralIntakeBranch';
import { DischargeSteps } from 'components/dischargeForm/helpers';
import { PreferredOptionLabel, PreferredProviderBadge } from 'components/preferredProviders/PreferredProviderLabels';
import usePreferredForGroupId from 'components/preferredProviders/usePreferredForGroupId';
import usePreferredSections from 'components/preferredProviders/usePreferredSections';
import ActivityInput from 'components/shared/activityInput/ActivityInput';
import useActivityInput, { ActivityInputValues } from 'components/shared/activityInput/useActivityInput';
import Alert from 'components/shared/Alert';
import Checkbox from 'components/shared/form/Checkbox';
import DatePicker from 'components/shared/form/DatePicker';
import FieldLabel from 'components/shared/form/FieldLabel';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import Input from 'components/shared/form/Input';
import InputGroup from 'components/shared/form/InputGroup';
import { useForm } from 'context/form';
import { useTranslation } from 'context/translations';
import Upload from 'hooks/useDirectUpload/Upload';
import { conjoin } from 'lib/util';
import Attachment from 'models/Attachment';
import DischargeReason from 'models/DischargeReason';
import Group from 'models/Group';
import LocationEpisode from 'models/LocationEpisode';
import { useInfiniteDischargeReasons } from 'services/api/dischargeReasons';
import { useInfiniteGroups } from 'services/api/group';
import { colors } from 'styles/theme/colors';

type Props = {
  locationEpisode: LocationEpisode;
  showActualDischargeDate?: boolean;
  hideDischargedAma?: boolean;
  taggable?: boolean;
  /** Attachments to merge into the note's attachment list once on arrival.
   * Used to surface async-fetched data (e.g. an auto-pulled CCD) inside the
   * `useActivityInput` local state which otherwise snapshots `values.note`
   * at mount and ignores later changes. */
  initialAttachments?: (Upload | Attachment)[];
};

export type DischargeFormValues = {
  id: string;
  dischargeReason: DischargeReason;
  dischargedGroup: Group;
  dischargedLocationOther: string;
  againstMedicalAdvice: boolean;
  actualDischargeDate: Date;
  note: ActivityInputValues;
  questions: Record<string, string>;
  linkedQuestions?: Record<string, Record<string, string>>;
};

export type DischargeFormMeta = {
  step: DischargeSteps;
  isSubmitting: boolean;
  isUploadingAttachments: boolean;
};

export default function DischargeInformation({
  locationEpisode,
  showActualDischargeDate = false,
  hideDischargedAma = false,
  taggable,
  initialAttachments,
}: Props) {
  const { initialValues, values, setValue, setMeta, errors, validateField } = useForm<
    DischargeFormValues,
    DischargeFormMeta
  >();
  const { note, setNote } = useActivityInput({ ...values.note });
  const { translate } = useTranslation();

  // Merge any externally-provided initial attachments into the local note
  // state once. `useActivityInput` captures `values.note` at mount in a ref,
  // so subsequent async arrivals (e.g. the auto-pulled CCD) need a one-shot
  // injection here. Subsequent removals by the user (via the trash icon on
  // the attachment chip) stay removed — the ref guard blocks re-injection.
  const initialAttachmentsMergedRef = useRef(false);
  useEffect(() => {
    if (initialAttachmentsMergedRef.current) return;
    if (!initialAttachments || initialAttachments.length === 0) return;
    initialAttachmentsMergedRef.current = true;
    setNote((prev) => {
      const existingIds = new Set(prev.attachments.map((a) => a.id));
      const additions = initialAttachments.filter((a) => !existingIds.has(a.id));
      if (additions.length === 0) return prev;
      return { ...prev, attachments: [...prev.attachments, ...additions] };
    });
  }, [initialAttachments, setNote]);

  const asyncDischargeReasons = useInfiniteDischargeReasons({
    include: 'dischargedToGroupType',
    sortBy: 'order asc',
    groupType: locationEpisode.group?.groupTypeId,
  });

  const selectedReason = values.dischargeReason;

  const allOwnerClientIds = [
    locationEpisode.owner.clientId,
    ...locationEpisode.sharedRecords.map((le) => le.owner.clientId),
  ].filter((id, index, arr) => id && arr.indexOf(id) === index);

  const preferredForGroupId = usePreferredForGroupId(
    locationEpisode.group?.id,
    selectedReason?.dischargedToGroupType?.apiName
  );
  const groupParams = {
    clientIdOrNetworkClientId: selectedReason?.dischargedToGroupType?.isManager
      ? undefined
      : allOwnerClientIds.join(','),
    groupType: selectedReason?.dischargedToGroupTypeId ?? undefined,
    'id.not': locationEpisode.group?.id,
    dischargeLocationEpisodeId: locationEpisode.id,
    search: '',
    sortBy: 'name asc',
    viewOnly: true,
    preferredForGroupId,
  };
  const asyncGroupOptions = useInfiniteGroups(groupParams, { enabled: !!preferredForGroupId });
  const { showLegend, isPreferred, sectionProps } = usePreferredSections(asyncGroupOptions.options, groupParams);

  const branchGuidance = useMemo(
    () => centralIntakeBranch(locationEpisode.referralReviews, selectedReason?.dischargedToGroupTypeId),
    [locationEpisode, selectedReason?.dischargedToGroupTypeId]
  );

  const isOtherLocation = values.dischargedGroup?.name === 'Other';
  const enteredAt = locationEpisode.currentRehabState.enteredAt;
  const isReadmission = selectedReason?.isReadmission;
  const notesLabel = isReadmission ? 'Readmission Details' : 'Notes (optional)';
  const selectedReasonInfoMessage = selectedReason?.infoMessage;
  const selectedReasonWarningMessage = selectedReason?.warningMessage;

  useEffect(() => {
    setValue('note', note);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [note]);

  const setDischargeFormValue = useCallback(
    (...args: Parameters<typeof setValue>) => {
      // If the value id matches the initial value id, use the initial value object
      // so that the form isn't dirty when the user selects the same value
      if (args[1]?.id && isEqual(args[1]?.id, initialValues[args[0]]?.id)) {
        args[1] = initialValues[args[0]];
      }

      setValue(...args);
    },
    [initialValues, setValue]
  );

  return (
    <>
      {showActualDischargeDate && (
        <InputGroup title={`Actual ${translate('Discharge')} Date`} error={errors.actualDischargeDate}>
          <DatePicker
            minDate={enteredAt ?? new Date()}
            maxDate={new Date()}
            selected={values.actualDischargeDate ?? null}
            onChange={(z) => {
              setValue('actualDischargeDate', z);
            }}
            $hasError={!!errors.actualDischargeDate}
            onBlur={() => validateField('actualDischargeDate', values)}
          />
        </InputGroup>
      )}
      <InputGroup title={`${translate('Discharged')} To`} error={errors.dischargeReason} data-cy='dischargeReason'>
        <Select<DischargeReason>
          {...asyncDischargeReasons}
          placeholder='Select'
          onChange={(change) => {
            setValue('dischargedGroup', null, { validate: false });
            setValue('dischargedLocationOther', '', { validate: false });
            setDischargeFormValue('dischargeReason', change);

            const { preselected } = centralIntakeBranch(
              locationEpisode.referralReviews,
              change?.dischargedToGroupTypeId
            );
            if (preselected) setDischargeFormValue('dischargedGroup', preselected, { validate: false });
          }}
          getOptionValue={(prop) => prop.id}
          getOptionLabel={(prop) => prop.displayName}
          value={values.dischargeReason}
        />
        {selectedReasonInfoMessage && (
          <Alert style={{ marginTop: 10 }}>
            <Alert.Text>{selectedReasonInfoMessage}</Alert.Text>
          </Alert>
        )}
      </InputGroup>
      {!hideDischargedAma && (
        <InputGroup>
          <Checkbox
            checked={values.againstMedicalAdvice || false}
            checkedColor={colors.blue100}
            size={16}
            labelSize='14px'
            label={`${translate('Discharged')} against medical advice (AMA)`}
            onChange={(val) => setValue('againstMedicalAdvice', val)}
          />
        </InputGroup>
      )}
      <InputGroup
        title={selectedReason?.dischargedToGroupType?.displayName}
        visible={!!selectedReason?.dischargedToGroupTypeId}
        error={errors.dischargedGroup}
        badge={showLegend(values.dischargedGroup) && <PreferredProviderBadge />}
        data-testid='discharged-group'>
        <Select
          {...asyncGroupOptions}
          {...sectionProps}
          appendedOptions={[new Group({ name: 'Other' })]}
          placeholder='Select a location'
          getOptionValue={(prop) => prop.id}
          getOptionLabel={(prop) => prop.name}
          formatOptionLabel={(prop: Group) => <PreferredOptionLabel label={prop.name} preferred={isPreferred(prop)} />}
          onChange={(change) => {
            setValue('dischargedLocationOther', '', { validate: false });
            setDischargeFormValue('dischargedGroup', change);
          }}
          value={values.dischargedGroup}
        />
        {branchGuidance.assignedBranches.length > 0 && (
          <Alert style={{ marginTop: 10 }} data-testid='central-intake-branch-note'>
            <Alert.Text>
              Central intake accepted this referral at{' '}
              {conjoin(branchGuidance.assignedBranches.map((branch) => branch.name))}, so the provider&apos;s other
              branches aren&apos;t offered.
            </Alert.Text>
          </Alert>
        )}
      </InputGroup>
      <InputGroup title='Other' visible={isOtherLocation} error={errors.dischargedLocationOther}>
        <Input
          value={values.dischargedLocationOther ?? ''}
          onChange={(event) => setValue('dischargedLocationOther', event.target.value)}
          $hasError={false}
          disabled={false}
        />
      </InputGroup>
      <FieldLabel>{notesLabel}</FieldLabel>
      <ActivityInput
        locationEpisodeId={locationEpisode.id}
        values={note}
        setValues={setNote}
        onUploading={(uploading) => setMeta('isUploadingAttachments', uploading)}
        taggable={taggable}
      />
      {selectedReasonWarningMessage && (
        <Alert style={{ marginTop: 10 }}>
          <Alert.Text>{selectedReasonWarningMessage}</Alert.Text>
        </Alert>
      )}
    </>
  );
}
